import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  PKG_NODE_ENTITY_TYPES,
  PKG_EDGE_TYPES,
  type PKGNodeEntityType,
  type PKGEdgeType,
  type LayoutHint,
} from '@blueprint/shared';
import { PKGCanvas } from '../components/pkg/PKGCanvas';
import type { MappedSubgraph } from '../components/pkg/mapSubgraphResponse';
import { fetchExplorerSubgraph } from '../components/pkg/fetchExplorerSubgraph';
import { DEFAULT_QUERY_STATE, type ExplorerQueryState } from '../components/pkg/explorerQueryState';
import {
  listPkgBookmarks,
  createPkgBookmark,
  deletePkgBookmark,
  type PkgBookmark,
} from '../components/pkg/pkgBookmarksClient';
import { ApiError } from '../lib/api';

// PKGV.B2 (#796) — <PKGExplorerPage />. See Sprint-B2-recon.md /
// Sprint-B2-adjudication.md / Sprint-B2-impl-prompt.md for the full
// adjudicated scope. Notably out of this cut: right-click context menu
// (Q3, no attachment point in <PKGCanvas />'s merged contract), a 4th
// layout (Q2, needs an A1 contract change), anchor-search UI (Q9).

const NODE_TYPE_LABELS: Record<PKGNodeEntityType, string> = {
  artefact: 'Artefacts',
  decision: 'Decisions',
  risk: 'Risks',
  issue: 'Issues',
  meeting: 'Meetings',
  requirement: 'Requirements',
  user_story: 'User Stories',
  adr_label: 'ADR Labels',
  risk_label: 'Risk Labels',
  issue_label: 'Issue Labels',
};

const EDGE_TYPE_LABELS: Record<PKGEdgeType, string> = {
  EVIDENCE_LINK: 'Evidence Link',
  DECISION_OF: 'Decision Of',
  RISK_OF: 'Risk Of',
  IMPLEMENTS: 'Implements',
  DERIVED_FROM: 'Derived From',
  REFERENCES: 'References',
};

const LAYOUT_LABELS: Record<LayoutHint, string> = {
  dagre: 'Dagre',
  radial: 'Radial',
  force: 'Force-directed',
};

// Q9: no anchor-search UI this cut, so `anchorIds` in ExplorerQueryState
// can only ever be non-empty via a bookmark that was created before an
// anchor-bearing node existed, or seeded directly (test fixture / manual
// row). Real-world this path is defined-but-dormant — V3 adjudication.
interface StaleBookmarkError {
  bookmark: PkgBookmark;
}

function isNotFoundError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

export default function PKGExplorerPage() {
  const { projectId } = useParams<{ projectId: string }>();

  const [queryState, setQueryState] = useState<ExplorerQueryState>(DEFAULT_QUERY_STATE);
  const [subgraph, setSubgraph] = useState<MappedSubgraph | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [staleBookmarkError, setStaleBookmarkError] = useState<StaleBookmarkError | null>(null);

  const [bookmarks, setBookmarks] = useState<PkgBookmark[]>([]);
  const [bookmarksError, setBookmarksError] = useState<string | null>(null);

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadBookmarks = useCallback(() => {
    if (!projectId) return;
    listPkgBookmarks(projectId)
      .then(setBookmarks)
      .catch(e => setBookmarksError(String(e)));
  }, [projectId]);

  useEffect(() => { loadBookmarks(); }, [loadBookmarks]);

  // R2/R3 adversarial fix: two rapid filter toggles fired before either
  // fetch resolves used to race on two axes — (a) both toggle handlers
  // read `queryState` from the render closure, which `runQuery` only
  // updates once its fetch RESOLVES, so a second toggle fired before the
  // first resolves computed its next selection from the pre-first-toggle
  // state, silently dropping the first toggle from what got sent; (b)
  // whichever fetch happened to resolve LAST won, regardless of which
  // toggle was fired last, so a slow earlier request could overwrite a
  // faster later one's (correct) result with stale data — checkboxes and
  // displayed graph (and truncation banner) could end up mismatched.
  //
  // Fixed with two refs: `latestQueryRef` always holds the most recently
  // INTENDED query (updated synchronously, not on fetch resolution) so
  // toggle handlers always compose on top of each other correctly;
  // `requestSeqRef` tags each fetch with a sequence number so a
  // response only gets applied if it's still the most recent request —
  // an out-of-order resolution is silently discarded instead of
  // overwriting newer state.
  const latestQueryRef = useRef<ExplorerQueryState>(DEFAULT_QUERY_STATE);
  const requestSeqRef = useRef(0);

  const runQuery = useCallback((query: ExplorerQueryState, bookmarkContext?: PkgBookmark) => {
    if (!projectId) return;
    latestQueryRef.current = query;
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    setStaleBookmarkError(null);
    fetchExplorerSubgraph(projectId, query)
      .then(result => {
        if (seq !== requestSeqRef.current) return; // superseded by a newer request
        setSubgraph(result);
        setQueryState(query);
      })
      .catch(e => {
        if (seq !== requestSeqRef.current) return; // superseded by a newer request
        // V3 adjudication: B1 rejects the WHOLE request when any anchorId
        // no longer exists. Only a bookmark-driven run can carry
        // anchorIds this cut (no anchor-search UI) — surface an
        // actionable error rather than a generic one, so the caller can
        // remove the stale bookmark or save a fresh view instead of
        // being stuck looking at a plain error message.
        if (bookmarkContext && isNotFoundError(e)) {
          setStaleBookmarkError({ bookmark: bookmarkContext });
        } else {
          setError(String(e));
        }
      })
      .finally(() => {
        if (seq !== requestSeqRef.current) return; // superseded by a newer request
        setLoading(false);
      });
  }, [projectId]);

  useEffect(() => {
    runQuery(DEFAULT_QUERY_STATE);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only; projectId change is a full remount via route param.
  }, [projectId]);

  const toggleNodeType = (type: PKGNodeEntityType) => {
    const current = latestQueryRef.current.nodeTypes ?? [];
    const next = current.includes(type) ? current.filter(t => t !== type) : [...current, type];
    runQuery({ ...latestQueryRef.current, nodeTypes: next });
  };

  const toggleEdgeType = (type: PKGEdgeType) => {
    const current = latestQueryRef.current.edgeTypes ?? [];
    const next = current.includes(type) ? current.filter(t => t !== type) : [...current, type];
    runQuery({ ...latestQueryRef.current, edgeTypes: next });
  };

  // AC4: layout switch is a pure client-side relayout — no re-fetch, no
  // network round-trip, keeps the <300ms switch target achievable.
  // Also updates latestQueryRef (not just queryState) — otherwise a
  // layout change followed by a filter toggle before any re-render would
  // silently resurrect the OLD layoutHint in the toggle's fetch, the
  // same stale-composition bug the request-race fix above guards
  // against, just on this field instead of nodeTypes/edgeTypes.
  const setLayout = (layoutHint: LayoutHint) => {
    latestQueryRef.current = { ...latestQueryRef.current, layoutHint };
    setQueryState(prev => ({ ...prev, layoutHint }));
  };

  const loadBookmark = (bookmark: PkgBookmark) => {
    runQuery(bookmark.queryParams, bookmark);
  };

  const removeBookmark = (bookmarkId: string) => {
    if (!projectId) return;
    deletePkgBookmark(projectId, bookmarkId)
      .then(() => {
        loadBookmarks();
        setStaleBookmarkError(null);
      })
      .catch(e => setBookmarksError(String(e)));
  };

  const openSaveDialog = () => {
    setSaveName('');
    setSaveError(null);
    setSaveDialogOpen(true);
  };

  const submitSave = () => {
    if (!projectId || !saveName.trim()) return;
    setSaving(true);
    setSaveError(null);
    createPkgBookmark(projectId, saveName.trim(), queryState)
      .then(() => {
        setSaveDialogOpen(false);
        setStaleBookmarkError(null);
        loadBookmarks();
      })
      .catch(e => setSaveError(String(e)))
      .finally(() => setSaving(false));
  };

  if (!projectId) return null;

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      <aside
        style={{
          width: 240, flexShrink: 0, borderRight: '1px solid var(--border)',
          background: 'var(--surface)', overflowY: 'auto', padding: 16,
        }}
      >
        <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 12px' }}>
          Knowledge Graph
        </h2>

        <FilterSection title="Entity types">
          {PKG_NODE_ENTITY_TYPES.map(type => (
            <FilterCheckbox
              key={type}
              label={NODE_TYPE_LABELS[type]}
              checked={(queryState.nodeTypes ?? []).includes(type)}
              onChange={() => toggleNodeType(type)}
            />
          ))}
        </FilterSection>

        <FilterSection title="Edge types">
          {PKG_EDGE_TYPES.map(type => (
            <FilterCheckbox
              key={type}
              label={EDGE_TYPE_LABELS[type]}
              checked={(queryState.edgeTypes ?? []).includes(type)}
              onChange={() => toggleEdgeType(type)}
            />
          ))}
        </FilterSection>

        <FilterSection title="Layout">
          <select
            value={queryState.layoutHint}
            onChange={e => setLayout(e.target.value as LayoutHint)}
            style={{
              width: '100%', padding: '6px 8px', borderRadius: 'var(--radius-md, 6px)',
              border: '1px solid var(--border)', background: 'var(--surface)',
              color: 'var(--text)', fontSize: 13,
            }}
          >
            {(['dagre', 'radial', 'force'] as LayoutHint[]).map(hint => (
              <option key={hint} value={hint}>{LAYOUT_LABELS[hint]}</option>
            ))}
          </select>
        </FilterSection>

        <FilterSection title="Saved queries">
          {bookmarksError && (
            <p style={{ fontSize: 12, color: 'var(--error, #dc2626)' }}>{bookmarksError}</p>
          )}
          {bookmarks.length === 0 && !bookmarksError && (
            <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No saved queries yet.</p>
          )}
          {bookmarks.map(b => (
            <div
              key={b.id}
              style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}
            >
              <button
                type="button"
                onClick={() => loadBookmark(b)}
                style={{
                  flex: 1, textAlign: 'left', padding: '4px 8px', fontSize: 13,
                  border: '1px solid var(--border)', borderRadius: 'var(--radius-md, 6px)',
                  background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer',
                }}
              >
                {b.name}
              </button>
              <button
                type="button"
                aria-label={`Delete bookmark ${b.name}`}
                onClick={() => removeBookmark(b.id)}
                style={{
                  border: 'none', background: 'transparent', color: 'var(--text-secondary)',
                  cursor: 'pointer', fontSize: 14, padding: '2px 6px',
                }}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={openSaveDialog}
            style={{
              width: '100%', marginTop: 8, padding: '6px 8px', fontSize: 13, fontWeight: 600,
              border: '1px solid var(--primary)', borderRadius: 'var(--radius-md, 6px)',
              background: 'transparent', color: 'var(--primary)', cursor: 'pointer',
            }}
          >
            Save current view as...
          </button>
          {saveDialogOpen && (
            <div style={{ marginTop: 8, padding: 8, border: '1px solid var(--border)', borderRadius: 'var(--radius-md, 6px)' }}>
              <input
                type="text"
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                placeholder="Bookmark name"
                maxLength={100}
                style={{
                  width: '100%', padding: '4px 8px', fontSize: 13, marginBottom: 6,
                  border: '1px solid var(--border)', borderRadius: 'var(--radius-md, 6px)',
                  background: 'var(--surface)', color: 'var(--text)',
                }}
              />
              {saveError && (
                <p style={{ fontSize: 12, color: 'var(--error, #dc2626)', margin: '0 0 6px' }}>{saveError}</p>
              )}
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  disabled={saving || !saveName.trim()}
                  onClick={submitSave}
                  style={{
                    flex: 1, padding: '4px 8px', fontSize: 13, fontWeight: 600,
                    border: 'none', borderRadius: 'var(--radius-md, 6px)',
                    background: 'var(--primary)', color: '#fff',
                    cursor: saving || !saveName.trim() ? 'not-allowed' : 'pointer',
                    opacity: saving || !saveName.trim() ? 0.6 : 1,
                  }}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setSaveDialogOpen(false)}
                  style={{
                    padding: '4px 8px', fontSize: 13, border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md, 6px)', background: 'transparent',
                    color: 'var(--text-secondary)', cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </FilterSection>
      </aside>

      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {subgraph?.truncated && (
          <div
            role="status"
            style={{
              padding: '8px 16px', fontSize: 13, background: 'rgba(245,158,11,0.12)',
              color: '#f59e0b', borderBottom: '1px solid var(--border)',
            }}
          >
            Showing the first {subgraph.nodes.length.toLocaleString()} nodes — narrow your filters to see more.
          </div>
        )}

        {staleBookmarkError && (
          <div
            role="alert"
            style={{
              padding: '12px 16px', fontSize: 13, background: 'rgba(220,38,38,0.08)',
              color: 'var(--text)', borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}
          >
            <span>
              One or more nodes in the saved view &ldquo;{staleBookmarkError.bookmark.name}&rdquo; no longer exist.
            </span>
            <button
              type="button"
              onClick={() => removeBookmark(staleBookmarkError.bookmark.id)}
              style={{
                padding: '4px 10px', fontSize: 12, fontWeight: 600, border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md, 6px)', background: 'transparent',
                color: 'var(--text)', cursor: 'pointer',
              }}
            >
              Remove this bookmark
            </button>
            <button
              type="button"
              onClick={() => { setStaleBookmarkError(null); openSaveDialog(); }}
              style={{
                padding: '4px 10px', fontSize: 12, fontWeight: 600, border: '1px solid var(--primary)',
                borderRadius: 'var(--radius-md, 6px)', background: 'transparent',
                color: 'var(--primary)', cursor: 'pointer',
              }}
            >
              Save current view instead
            </button>
          </div>
        )}

        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
              Loading graph...
            </div>
          )}

          {!loading && error && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <p style={{ fontSize: 13, color: 'var(--error, #dc2626)' }}>{error}</p>
              <button
                type="button"
                onClick={() => runQuery(queryState)}
                style={{
                  padding: '4px 10px', fontSize: 12, fontWeight: 600, border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md, 6px)', background: 'transparent',
                  color: 'var(--text)', cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && subgraph && subgraph.nodes.length === 0 && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Your graph is empty</p>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                No nodes match the current filters yet.
              </p>
            </div>
          )}

          {!loading && !error && subgraph && subgraph.nodes.length > 0 && (
            <PKGCanvas
              nodes={subgraph.nodes}
              edges={subgraph.edges}
              layoutHint={queryState.layoutHint}
              interactionLevel="interactive"
            />
          )}
        </div>
      </main>
    </div>
  );
}

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)', margin: '0 0 8px' }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function FilterCheckbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span style={{ fontSize: 13, color: 'var(--text)' }}>{label}</span>
    </label>
  );
}
